/**
 * Open Questions Store (Workspace-Scoped)
 *
 * Manages workspace-level open questions. Unlike PorOpenQuestions
 * (which is scoped to a Plan of Record), these questions float freely
 * at the workspace level and can optionally be anchored to a domain
 * entity (item, cycle, session, track) via contextEntityType/Id.
 *
 * Status lifecycle:
 *   open → in_progress → resolved | deferred
 *   resolved | deferred → open  (reopen)
 *
 * Per Build Contract: Supabase is source of truth. Zustand is a cache.
 */

import { create } from 'zustand';
import {
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
  generateUUID,
  createInitialSyncState,
} from '../../lib/supabaseSync';
import type { SyncState } from '../../lib/supabaseSync';

// ── Types ─────────────────────────────────────────────────────

export type OpenQuestionStatus = 'open' | 'in_progress' | 'resolved' | 'deferred';

export type OpenQuestionContextType =
  // Prioritization domain entities (original)
  | 'item'
  | 'cycle'
  | 'session'
  | 'track'
  // Clarity tool entities (extended per plan)
  | 'outcome_driver'
  | 'desired_outcome'
  | 'backplan_node'
  | 'roadmap_node'
  | 'ost_node';

export interface OpenQuestion {
  id: string;
  workspaceId: string;
  // Core framework fields
  text: string;
  priority: number | null;          // stack rank; 1 = Focusing Question
  currentTheory: string | null;     // "What do we think now?"
  sourceOfAnswer: string | null;    // "Who has a definitive answer?"
  stepsToAnswer: string | null;     // "Action steps to get the real answer"
  // Status & resolution
  status: OpenQuestionStatus;
  resolutionText: string | null;    // "What's the real answer?" — filled on resolve
  resolvedAt: string | null;
  resolvedBy: string | null;
  // Context anchor
  contextEntityType: OpenQuestionContextType | null;
  contextEntityId: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export const STATUS_TRANSITIONS: Record<OpenQuestionStatus, OpenQuestionStatus[]> = {
  open: ['in_progress', 'resolved', 'deferred'],
  in_progress: ['open', 'resolved', 'deferred'],
  resolved: ['open'],
  deferred: ['open'],
};

export interface CreateQuestionInput {
  text: string;
  priority?: number | null;
  currentTheory?: string | null;
  sourceOfAnswer?: string | null;
  stepsToAnswer?: string | null;
  resolutionText?: string | null;
  contextEntityType?: OpenQuestionContextType | null;
  contextEntityId?: string | null;
}

export type QuestionFrameworkUpdate = Partial<Pick<OpenQuestion,
  'text' | 'priority' | 'currentTheory' | 'sourceOfAnswer' | 'stepsToAnswer' |
  'resolutionText' | 'contextEntityType' | 'contextEntityId'
>>;

// ── Store interface ───────────────────────────────────────────

interface OpenQuestionsState {
  questions: OpenQuestion[];
  syncState: SyncState;

  fetchFromSupabase: () => Promise<void>;

  createQuestion: (input: CreateQuestionInput) => Promise<{ question: OpenQuestion | null; error?: string }>;
  updateQuestion: (id: string, updates: QuestionFrameworkUpdate) => Promise<{ success: boolean; error?: string }>;
  deleteQuestion: (id: string) => Promise<{ success: boolean; error?: string }>;
  transitionStatus: (id: string, newStatus: OpenQuestionStatus, resolutionText?: string) => Promise<{ success: boolean; error?: string }>;
  /** Bulk-assign priorities after drag-reorder; ids in desired order → priority 1…n */
  reprioritize: (orderedIds: string[]) => Promise<{ success: boolean; error?: string }>;

  getQuestionById: (id: string) => OpenQuestion | undefined;
  getQuestionsForEntity: (entityType: OpenQuestionContextType, entityId: string) => OpenQuestion[];
  getUnanchoredQuestions: () => OpenQuestion[];
}

// ── Helpers ───────────────────────────────────────────────────

function rowToQuestion(r: Record<string, unknown>): OpenQuestion {
  return {
    id: r.id as string,
    workspaceId: r.workspace_id as string,
    text: (r.text as string) ?? '',
    priority: (r.priority as number) ?? null,
    currentTheory: (r.current_theory as string) ?? null,
    sourceOfAnswer: (r.source_of_answer as string) ?? null,
    stepsToAnswer: (r.steps_to_answer as string) ?? null,
    status: ((r.status as string) ?? 'open') as OpenQuestionStatus,
    resolutionText: (r.resolution_text as string) ?? null,
    resolvedAt: (r.resolved_at as string) ?? null,
    resolvedBy: (r.resolved_by as string) ?? null,
    contextEntityType: (r.context_entity_type as OpenQuestionContextType) ?? null,
    contextEntityId: (r.context_entity_id as string) ?? null,
    createdBy: (r.created_by as string) ?? null,
    createdAt: r.created_at as string,
    updatedAt: r.updated_at as string,
  };
}

// ── Store ─────────────────────────────────────────────────────

export const useOpenQuestionsStore = create<OpenQuestionsState>()((set, get) => ({
  questions: [],
  syncState: createInitialSyncState(),

  fetchFromSupabase: async () => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return;

    set({ syncState: { ...get().syncState, isLoading: true, syncError: null } });

    const { data, error } = await supabase
      .from('open_questions')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('priority', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[OpenQuestionsStore] fetchFromSupabase error:', error);
      set({ syncState: { isLoading: false, syncError: error.message, lastSyncAt: null } });
      return;
    }

    set({
      questions: (data ?? []).map((r) => rowToQuestion(r as Record<string, unknown>)),
      syncState: { isLoading: false, syncError: null, lastSyncAt: new Date().toISOString() },
    });
  },

  createQuestion: async (input) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return { question: null, error: 'No Supabase client or workspace' };

    const { data, error } = await supabase
      .from('open_questions')
      .insert({
        id: generateUUID(),
        workspace_id: workspaceId,
        text: input.text,
        priority: input.priority ?? null,
        current_theory: input.currentTheory ?? null,
        source_of_answer: input.sourceOfAnswer ?? null,
        steps_to_answer: input.stepsToAnswer ?? null,
        resolution_text: input.resolutionText ?? null,
        context_entity_type: input.contextEntityType ?? null,
        context_entity_id: input.contextEntityId ?? null,
      })
      .select('*')
      .single();

    if (error) {
      console.error('[OpenQuestionsStore] createQuestion error:', error);
      return { question: null, error: error.message };
    }

    const question = rowToQuestion(data as Record<string, unknown>);
    set((state) => ({ questions: [question, ...state.questions] }));
    return { question };
  },

  updateQuestion: async (id, updates) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const dbUpdates: Record<string, unknown> = {};
    if (updates.text !== undefined) dbUpdates.text = updates.text;
    if (updates.priority !== undefined) dbUpdates.priority = updates.priority;
    if (updates.currentTheory !== undefined) dbUpdates.current_theory = updates.currentTheory;
    if (updates.sourceOfAnswer !== undefined) dbUpdates.source_of_answer = updates.sourceOfAnswer;
    if (updates.stepsToAnswer !== undefined) dbUpdates.steps_to_answer = updates.stepsToAnswer;
    if (updates.resolutionText !== undefined) dbUpdates.resolution_text = updates.resolutionText;
    if (updates.contextEntityType !== undefined) dbUpdates.context_entity_type = updates.contextEntityType;
    if (updates.contextEntityId !== undefined) dbUpdates.context_entity_id = updates.contextEntityId;

    set((state) => ({
      questions: state.questions.map((q) => (q.id === id ? { ...q, ...updates } : q)),
    }));

    const { error } = await supabase.from('open_questions').update(dbUpdates).eq('id', id);
    if (error) {
      console.error('[OpenQuestionsStore] updateQuestion error:', error);
      await get().fetchFromSupabase();
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  deleteQuestion: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const prev = get().questions;
    set((state) => ({ questions: state.questions.filter((q) => q.id !== id) }));

    const { error } = await supabase.from('open_questions').delete().eq('id', id);
    if (error) {
      console.error('[OpenQuestionsStore] deleteQuestion error:', error);
      set({ questions: prev });
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  transitionStatus: async (id, newStatus, resolutionText) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const question = get().questions.find((q) => q.id === id);
    if (!question) return { success: false, error: 'Question not found' };

    const allowed = STATUS_TRANSITIONS[question.status];
    if (!allowed.includes(newStatus)) {
      return { success: false, error: `Cannot transition from ${question.status} to ${newStatus}` };
    }

    if (newStatus === 'resolved' && !resolutionText?.trim()) {
      return { success: false, error: 'Resolution text is required when resolving a question' };
    }

    const now = new Date().toISOString();
    const dbUpdates: Record<string, unknown> = { status: newStatus };
    if (newStatus === 'resolved') {
      dbUpdates.resolution_text = resolutionText ?? null;
      dbUpdates.resolved_at = now;
    }
    if (newStatus === 'open' && question.status !== 'open') {
      // Reopen: clear resolution fields
      dbUpdates.resolution_text = null;
      dbUpdates.resolved_at = null;
      dbUpdates.resolved_by = null;
    }

    set((state) => ({
      questions: state.questions.map((q) =>
        q.id === id
          ? {
              ...q,
              status: newStatus,
              resolutionText: (dbUpdates.resolution_text as string) ?? q.resolutionText,
              resolvedAt: (dbUpdates.resolved_at as string) ?? q.resolvedAt,
            }
          : q
      ),
    }));

    const { error } = await supabase.from('open_questions').update(dbUpdates).eq('id', id);
    if (error) {
      console.error('[OpenQuestionsStore] transitionStatus error:', error);
      await get().fetchFromSupabase();
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  reprioritize: async (orderedIds) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    // Optimistic update
    set((state) => ({
      questions: state.questions.map((q) => {
        const idx = orderedIds.indexOf(q.id);
        return idx === -1 ? q : { ...q, priority: idx + 1 };
      }),
    }));

    // Batch upserts — one update per question
    const updates = orderedIds.map((id, idx) =>
      supabase.from('open_questions').update({ priority: idx + 1 }).eq('id', id)
    );
    const results = await Promise.all(updates);
    const firstError = results.find((r) => r.error)?.error;
    if (firstError) {
      console.error('[OpenQuestionsStore] reprioritize error:', firstError);
      await get().fetchFromSupabase();
      return { success: false, error: firstError.message };
    }
    return { success: true };
  },

  getQuestionById: (id) => get().questions.find((q) => q.id === id),

  getQuestionsForEntity: (entityType, entityId) =>
    get().questions.filter(
      (q) => q.contextEntityType === entityType && q.contextEntityId === entityId
    ),

  getUnanchoredQuestions: () =>
    get().questions.filter((q) => q.contextEntityId === null),
}));
